import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Search, Trash2, Users2 } from 'lucide-react';
import api from '../../config/api';
import toast from 'react-hot-toast';

export default function Guardians() {
  const [links, setLinks] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  const doFetch = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (search) params.search = search;
      const res = await api.get('/guardians', { params });
      setLinks(res.data.data || []);
    } finally { setLoading(false); }
  }, [search]);

  useEffect(() => { doFetch(); }, [doFetch]);

  const unlink = async (id: string) => {
    if (!window.confirm('Remove this guardian link? The parent will lose access to this student.')) return;
    try {
      await api.delete(`/guardians/${id}`);
      toast.success('Guardian unlinked');
      doFetch();
    } catch (err: any) { toast.error(err?.response?.data?.message || 'Failed to unlink'); }
  };

  return (
    <div className="fade-in">
      <div className="page-header">
        <div><h1 className="page-title">Guardians</h1><p className="page-subtitle">Parent/guardian portal accounts and which students they can view</p></div>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}><Plus size={16} />Link Guardian</button>
      </div>

      <div className="card">
        <div className="filters-bar">
          <div className="search-bar"><Search size={15} style={{ color: 'var(--text-muted)' }} /><input placeholder="Search by guardian or student name..." value={search} onChange={e => setSearch(e.target.value)} /></div>
        </div>
        <div className="table-container">
          <table>
            <thead><tr><th>Guardian</th><th>Contact</th><th>Student</th><th>Relationship</th><th>Linked</th><th></th></tr></thead>
            <tbody>
              {loading ? <tr><td colSpan={6} style={{ textAlign: 'center', padding: 40 }}><div className="loader loader-dark" style={{ margin: '0 auto' }} /></td></tr>
              : links.length === 0 ? <tr><td colSpan={6}><div className="empty-state"><Users2 size={40} /><p>No guardians linked yet</p></div></td></tr>
              : links.map((l: any) => (
                <tr key={l.id}>
                  <td style={{ fontWeight: 500 }}>{l.parent_first_name} {l.parent_last_name}</td>
                  <td style={{ fontSize: 12 }}>{l.parent_email}{l.parent_phone ? ` · ${l.parent_phone}` : ''}</td>
                  <td><div style={{ fontWeight: 500 }}>{l.student_first_name} {l.student_last_name}</div><div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{l.student_number} · {l.program_name || 'No program'}</div></td>
                  <td style={{ textTransform: 'capitalize' }}>{l.relationship || '—'}</td>
                  <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{new Date(l.created_at).toLocaleDateString()}</td>
                  <td><button className="btn btn-secondary btn-sm" onClick={() => unlink(l.id)}><Trash2 size={13} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && <LinkGuardianModal onClose={() => setShowModal(false)} onSaved={() => { setShowModal(false); doFetch(); }} />}
    </div>
  );
}

function LinkGuardianModal({ onClose, onSaved }: any) {
  const [form, setForm] = useState<any>({ relationship: 'Parent' });
  const [loading, setLoading] = useState(false);
  const [searchStu, setSearchStu] = useState('');
  const [stuResults, setStuResults] = useState<any[]>([]);
  const [createdInfo, setCreatedInfo] = useState<any>(null);
  const set = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (searchStu.length >= 2) api.get('/students', { params: { search: searchStu, limit: 10 } }).then(r => setStuResults(r.data.data.students));
    else setStuResults([]);
  }, [searchStu]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.student_id) { toast.error('Select a student'); return; }
    if (!form.email) { toast.error('Guardian email is required'); return; }
    setLoading(true);
    try {
      const res = await api.post('/guardians', form);
      if (res.data.data.temp_password) {
        setCreatedInfo({ email: form.email, password: res.data.data.temp_password });
      } else {
        toast.success('Guardian linked');
        onSaved();
      }
    } catch (err: any) { toast.error(err?.response?.data?.message || 'Failed to link guardian'); }
    finally { setLoading(false); }
  };

  if (createdInfo) {
    return (
      <div className="modal-overlay" onClick={onSaved}>
        <div className="modal" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
          <div className="modal-header"><h3 style={{ fontWeight: 600 }}>Guardian Account Created</h3></div>
          <div className="modal-body">
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12 }}>Share these login details with the guardian. They should change the password after first login.</p>
            <div style={{ padding: 14, background: 'var(--bg)', borderRadius: 10 }}>
              <div style={{ fontSize: 13 }}>Email: <strong className="mono">{createdInfo.email}</strong></div>
              <div style={{ fontSize: 13, marginTop: 6 }}>Temp Password: <strong className="mono">{createdInfo.password}</strong></div>
            </div>
          </div>
          <div className="modal-footer"><button className="btn btn-primary" onClick={onSaved}>Done</button></div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 520 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h3 style={{ fontWeight: 600 }}>Link Guardian to Student</h3><button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20 }}>×</button></div>
        <form onSubmit={submit}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="form-group">
              <label className="form-label">Student *</label>
              <input className="form-input" placeholder="Type name or student number..." value={searchStu} onChange={e => setSearchStu(e.target.value)} />
              {stuResults.length > 0 && (
                <div style={{ border: '1px solid var(--border)', borderRadius: 8, maxHeight: 180, overflowY: 'auto', marginTop: 4 }}>
                  {stuResults.map((s: any) => (
                    <div key={s.id} onClick={() => { set('student_id', s.id); setSearchStu(`${s.first_name} ${s.last_name} (${s.student_number})`); setStuResults([]); }}
                      style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                      <strong>{s.first_name} {s.last_name}</strong> — {s.student_number}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>If this email already has a guardian account, it will just be linked to this student too. Otherwise a new guardian account is created.</p>
            <div className="form-group"><label className="form-label">Guardian Email *</label><input type="email" className="form-input" required value={form.email || ''} onChange={e => set('email', e.target.value)} /></div>
            <div className="grid-2" style={{ gap: 12 }}>
              <div className="form-group"><label className="form-label">First Name</label><input className="form-input" value={form.first_name || ''} onChange={e => set('first_name', e.target.value)} /></div>
              <div className="form-group"><label className="form-label">Last Name</label><input className="form-input" value={form.last_name || ''} onChange={e => set('last_name', e.target.value)} /></div>
            </div>
            <div className="grid-2" style={{ gap: 12 }}>
              <div className="form-group"><label className="form-label">Phone</label><input className="form-input" value={form.phone || ''} onChange={e => set('phone', e.target.value)} /></div>
              <div className="form-group"><label className="form-label">Relationship</label><select className="form-input form-select" value={form.relationship} onChange={e => set('relationship', e.target.value)}><option value="Parent">Parent</option><option value="Guardian">Guardian</option><option value="Sponsor">Sponsor</option></select></div>
            </div>
          </div>
          <div className="modal-footer"><button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button><button type="submit" className="btn btn-primary" disabled={loading}>{loading ? <span className="loader" /> : 'Link Guardian'}</button></div>
        </form>
      </div>
    </div>
  );
}
